import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, errMsg, useApi } from "../lib/api";
import { fmtCoords } from "../lib/labels";
import { PageHeader } from "../components/Layout";
import { ConfirmDialog, EmptyRow, Field, FormError, StatusBadge } from "../components/Common";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const NUMS = ["cx", "cy", "cz", "radius", "tx", "ty", "tz"];

const toForm = (z) => ({
  name: z?.name ?? "",
  cx: z?.coords?.x ?? "", cy: z?.coords?.y ?? "", cz: z?.coords?.z ?? "", radius: z?.radius ?? 50,
  tx: z?.craft_table?.x ?? "", ty: z?.craft_table?.y ?? "", tz: z?.craft_table?.z ?? "",
  owner: z?.owner_org_id ?? "none", active: z?.active ?? true,
});

const toPayload = (f) => ({
  name: f.name,
  coords: { x: +f.cx, y: +f.cy, z: +f.cz }, radius: +f.radius,
  craft_table: { x: +f.tx, y: +f.ty, z: +f.tz },
  owner_org_id: f.owner === "none" ? null : f.owner, active: f.active,
});

const XYZ = ({ title, prefix, form, set, testid }) => (
  <div>
    <div className="eyebrow mb-2 text-slate-400">{title}</div>
    <div className="grid grid-cols-3 gap-3">
      {["x", "y", "z"].map((a) => (
        <Field key={a} label={a.toUpperCase()} type="number" step="0.1" value={form[`${prefix}${a}`]} onChange={set(`${prefix}${a}`)} testid={`${testid}-${a}-input`} />
      ))}
    </div>
  </div>
);

const ZoneDialog = ({ zone, orgs, onClose, onSaved }) => {
  const isNew = !zone?.id;
  const [form, setForm] = useState(toForm(null));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(toForm(zone)); setError(""); }, [zone]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError("Nome: é obrigatório");
    if (NUMS.some((k) => form[k] === "" || Number.isNaN(Number(form[k])))) return setError("Preencha todos os campos numéricos com valores válidos.");
    setBusy(true);
    try {
      if (isNew) await api.post("/admin/zones", toPayload(form));
      else await api.put(`/admin/zones/${zone.id}`, toPayload(form));
      toast.success(isNew ? "Zona criada." : "Zona atualizada.");
      onSaved();
    } catch (err) {
      setError(errMsg(err, "Erro ao guardar zona."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!zone} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-slate-800 bg-slate-950" data-testid="zone-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display uppercase tracking-wider">{isNew ? "Nova zona" : "Editar zona"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5" noValidate>
          <div className="grid grid-cols-[2fr_1fr] gap-3">
            <Field label="Nome" value={form.name} onChange={set("name")} testid="zone-name-input" placeholder="ex: Mesa de Armas" />
            <Field label="Raio (m)" type="number" value={form.radius} onChange={set("radius")} testid="zone-radius-input" />
          </div>
          <XYZ title="Coordenadas da zona" prefix="c" form={form} set={set} testid="zone-coords" />
          <XYZ title="Mesa de craft" prefix="t" form={form} set={set} testid="zone-craft" />
          <p className="border border-slate-800 bg-slate-900/60 px-3 py-2 font-mono text-[11px] text-slate-400" data-testid="zone-times-note">
            Tempos de negociação/combate: configuráveis pela staff em <b className="text-slate-200">Config.Zones</b> no config.lua do recurso.
          </p>
          <div className="grid grid-cols-2 items-end gap-3">
            <div className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Dono atual</Label>
              <Select value={form.owner} onValueChange={(v) => setForm({ ...form, owner: v })}>
                <SelectTrigger className="border-slate-700 bg-slate-900" data-testid="zone-owner-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="zone-owner-option-none">Sem dono</SelectItem>
                  {(orgs || []).map((o) => <SelectItem key={o.id} value={o.id} data-testid={`zone-owner-option-${o.name}`}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex h-10 items-center gap-3 border border-slate-800 px-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="zone-active-switch" />
              <span className="text-sm">{form.active ? "Ativa" : "Inativa"}</span>
            </label>
          </div>
          <FormError message={error} testid="zone-form-error" />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-700 bg-transparent" data-testid="zone-cancel-button">Cancelar</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-500 text-slate-950 hover:bg-emerald-400" data-testid="zone-save-button">Guardar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ZoneRow = ({ z, onEdit, onDelete, onToggle }) => (
  <tr className="row-hover border-b border-slate-800/70 hover:bg-slate-900/60" data-testid={`zone-row-${z.id}`}>
    <td className="px-4 py-3 font-medium">{z.name}</td>
    <td className="px-4 py-3 text-sm" data-testid={`zone-owner-${z.id}`}>{z.owner ? <span className="text-emerald-300">{z.owner.label}</span> : <span className="text-slate-500">Sem dono</span>}</td>
    <td className="px-4 py-3 font-mono text-xs text-slate-400">{fmtCoords(z.coords)}<div className="text-slate-600">r = {z.radius} m</div></td>
    <td className="px-4 py-3 font-mono text-xs text-slate-400">{fmtCoords(z.craft_table)}</td>
    <td className="px-4 py-3">{z.active_war ? <StatusBadge status={z.active_war.status} testid={`zone-war-status-${z.id}`} /> : <span className="font-mono text-xs text-slate-600">—</span>}</td>
    <td className="px-4 py-3"><Switch checked={z.active} onCheckedChange={() => onToggle(z)} data-testid={`zone-active-toggle-${z.id}`} /></td>
    <td className="px-4 py-3">
      <div className="flex justify-end gap-1">
        <Button size="icon" variant="ghost" onClick={() => onEdit(z)} data-testid={`zone-edit-${z.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => onDelete(z)} data-testid={`zone-delete-${z.id}`}><Trash2 className="h-4 w-4 text-rose-400" /></Button>
      </div>
    </td>
  </tr>
);

export default function ZonesPage() {
  const { data: zones, reload } = useApi("/admin/zones");
  const { data: orgs } = useApi("/admin/orgs");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const run = async (fn, ok) => {
    try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Territórios & bancadas" title="Zonas">
        <Button onClick={() => setEditing({})} className="bg-emerald-500 text-slate-950 hover:bg-emerald-400" data-testid="create-zone-button"><Plus className="mr-1 h-4 w-4" />Nova zona</Button>
      </PageHeader>
      <div className="panel fade-up overflow-x-auto">
        <table className="w-full text-left" data-testid="zones-table">
          <thead className="border-b border-slate-800 font-mono text-[11px] uppercase tracking-widest text-slate-500">
            <tr><th className="px-4 py-3">Zona</th><th className="px-4 py-3">Dono</th><th className="px-4 py-3">Coordenadas</th><th className="px-4 py-3">Mesa de craft</th><th className="px-4 py-3">Guerra</th><th className="px-4 py-3">Ativa</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {zones && zones.length === 0 && <EmptyRow cols={7} text="Sem zonas." testid="zones-empty" />}
            {(zones || []).map((z) => (
              <ZoneRow key={z.id} z={z} onEdit={setEditing} onDelete={setDeleting}
                onToggle={(zone) => run(() => api.put(`/admin/zones/${zone.id}`, { active: !zone.active }), zone.active ? "Zona desativada." : "Zona ativada.")} />
            ))}
          </tbody>
        </table>
      </div>
      <ZoneDialog zone={editing} orgs={orgs} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} testid="zone-delete-dialog"
        title="Eliminar zona?" confirmLabel="Eliminar" description={`A zona ${deleting?.name || ""} será eliminada permanentemente.`}
        onConfirm={() => { run(() => api.delete(`/admin/zones/${deleting.id}`), "Zona eliminada."); setDeleting(null); }} />
    </div>
  );
}
